<template>
	<v-card class="flex round-card background-2 w100p dms-component" elevation="1">
		<v-card-title class="pa-3">
			<p class="pl-2 pl-xl-3">Area Cliente</p>
			<v-spacer></v-spacer>
			<v-spacer></v-spacer>
			<v-row class="pa-0 ma-0 pr-4">
				<v-text-field class="hidden-xs-only font-weight-regular"
					append-icon="search"
					label="Cerca"
					v-model="search"
					clearable
					hide-details outlined dense
				></v-text-field>
			</v-row>
			<v-tooltip top>
				<template v-slot:activator="{ on }">
					<v-btn @click="loadContent()" :loading="loading" v-on="on" icon>
						<v-icon>refresh</v-icon>
					</v-btn>
				</template>
				<span>Refresh</span>
			</v-tooltip>
		</v-card-title>
		<v-divider></v-divider>
		<v-container fluid class="pa-4">
		<!-- Toolbar -->
		<v-row no-gutters class="mb-3 align-center">
			<v-col cols="12" md="6">
				<v-breadcrumbs :items="breadcrumbItems" class="pa-0">
					<template v-slot:item="{ item }">
						<v-breadcrumbs-item
							:disabled="item.disabled"
							@click="navigateToFolder(item.folderId)"
							class="cursor-pointer"
						>
							{{ item.text }}
						</v-breadcrumbs-item>
					</template>
				</v-breadcrumbs>
			</v-col>
			<v-col cols="12" md="6" class="d-flex justify-end align-center">
				<v-btn-toggle v-model="viewMode" mandatory dense class="mr-2">
					<v-btn small value="grid"><v-icon small>mdi-view-grid</v-icon></v-btn>
					<v-btn small value="list"><v-icon small>mdi-view-list</v-icon></v-btn>
				</v-btn-toggle>
				<v-btn color="primary" small @click="newFolderDialog = true" class="mr-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-plus</v-icon>
					Nuova Cartella
				</v-btn>
				<v-btn color="info" small @click="importDialog = true" class="mr-2">
					<v-icon left small>mdi-bookshelf</v-icon>
					Importa da File Condivisi
				</v-btn>
				<v-btn color="success" small class="upload-btn">
					<v-icon left small class="d-none d-sm-inline">mdi-upload</v-icon>
					Carica
					<input
						ref="fileInput"
						type="file"
						multiple
						class="upload-btn-input"
						@change="handleFileUpload"
					/>
				</v-btn>
				<v-btn color="success" small outlined class="upload-btn ml-2">
					<v-icon left small class="d-none d-sm-inline">mdi-folder-upload</v-icon>
					Carica cartella
					<input
						ref="folderInput"
						type="file"
						webkitdirectory
						multiple
						class="upload-btn-input"
						@change="handleFolderUpload"
					/>
				</v-btn>
			</v-col>
		</v-row>

		<!-- Search scope toggle -->
		<v-row v-if="search" no-gutters class="mb-2 align-center">
			<v-chip-group v-model="searchScope" mandatory>
				<v-chip small filter value="local">Cartella corrente</v-chip>
				<v-chip small filter value="global">Ovunque</v-chip>
			</v-chip-group>
		</v-row>

		<!-- Area filter + storage -->
		<v-row no-gutters class="mb-2 align-center" style="min-height: 40px">
			<v-col v-if="!currentFolderId">
				<v-chip-group>
					<v-chip :outlined="areaFilter !== 'all'" small @click="areaFilter = 'all'">Tutti</v-chip>
					<v-chip :outlined="areaFilter !== 'tenant'" small @click="areaFilter = 'tenant'" color="blue" :text-color="areaFilter === 'tenant' ? 'white' : 'blue'">
						<v-icon left x-small>mdi-office-building</v-icon>Caricati da {{ appTitle }}
					</v-chip>
					<v-chip :outlined="areaFilter !== 'client'" small @click="areaFilter = 'client'" color="green" :text-color="areaFilter === 'client' ? 'white' : 'green'">
						<v-icon left x-small>mdi-account</v-icon>Caricati dal cliente
					</v-chip>
				</v-chip-group>
			</v-col>
			<v-spacer></v-spacer>
			<v-col cols="auto" class="text-caption grey--text">
				<v-icon x-small class="mr-1">mdi-harddisk</v-icon>{{ formatSize(filteredStorageSize) }}<span v-if="storage.quota"> / {{ storage.quota }} MB</span>
			</v-col>
		</v-row>

		<!-- Bulk toolbar -->
		<v-card v-if="hasSelection" outlined class="mb-3 bulk-toolbar">
			<v-card-text class="d-flex align-center py-2">
				<v-icon class="mr-2" color="primary">mdi-checkbox-multiple-marked</v-icon>
				<span class="text-body-2">{{ selectionCount }} elemento/i selezionato/i</span>
				<v-spacer></v-spacer>
				<v-btn small text @click="openBulkMoveDialog"><v-icon left small>mdi-folder-move</v-icon>Sposta</v-btn>
				<v-btn small text @click="bulkDownload" :loading="bulkLoading"><v-icon left small>mdi-download</v-icon>Scarica ZIP</v-btn>
				<v-btn small text color="error" @click="bulkDeleteDialog = true"><v-icon left small>mdi-delete</v-icon>Elimina</v-btn>
				<v-btn small icon @click="clearSelection"><v-icon>mdi-close</v-icon></v-btn>
			</v-card-text>
		</v-card>

		<!-- Drop zone -->
		<div
			ref="dropZone"
			class="drop-zone"
			:class="{ 'drop-zone-active': isDragging, 'marquee-active': marquee.active }"
			style="max-height: 500px; overflow-y: auto; position: relative"
			@dragenter.prevent="onDragEnter"
			@dragover.prevent
			@dragleave.prevent="onDragLeave"
			@drop.prevent="handleDrop"
			@mousedown.left="onMarqueeMouseDown"
		>
			<!-- Marquee overlay -->
			<div v-if="marquee.active" class="marquee-rect" :style="{
				left: marquee.x + 'px',
				top: marquee.y + 'px',
				width: marquee.w + 'px',
				height: marquee.h + 'px'
			}"></div>
			<!-- Upload progress -->
			<v-progress-linear v-if="uploading" :value="uploadProgress" color="primary" class="mb-3" height="6" rounded></v-progress-linear>

			<!-- Cartelle -->
			<div v-if="filteredFolders.length && !isGlobalSearch" class="mb-4">
				<div class="text-overline grey--text mb-2">Cartelle</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="folder in filteredFolders" :key="'f-' + folder.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="text-center pa-3 cursor-pointer hover-card d-flex flex-column align-center justify-center"
							style="height: 140px"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id, 'selected-multi': selectedFolderIds.includes(folder.id) }"
							:data-folder-id="folder.id"
							draggable="true"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
						>
							<v-icon size="48" color="amber darken-2">mdi-folder</v-icon>
							<div class="text-body-2 mt-1 d-flex align-center justify-center" style="max-width: 100%">
								<span class="text-truncate" style="flex: 1; min-width: 0">{{ folder.name }}</span>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small :color="folder.area === 'tenant' ? 'blue' : 'green'" class="ml-1 flex-shrink-0">mdi-circle</v-icon>
									</template>
									<span>{{ folder.area === 'tenant' ? 'Caricati da ' + appTitle : 'Caricati dal cliente' }}</span>
								</v-tooltip>
								<v-tooltip bottom v-if="folder.portal_shares_count > 0">
									<template v-slot:activator="{ on }">
										<v-badge :content="folder.portal_shares_count" color="teal" inline v-on="on" class="ml-1 flex-shrink-0">
											<v-icon x-small color="teal">mdi-account-multiple</v-icon>
										</v-badge>
									</template>
									<span>Condiviso con {{ folder.portal_shares_count }} utente/i del portale</span>
								</v-tooltip>
							</div>
							<div class="text-caption grey--text">{{ folder.documents_count || 0 }} file<span v-if="folder.documents_sum_size"> · {{ formatSize(folder.documents_sum_size) }}</span></div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<tbody>
						<tr
							v-for="folder in filteredFolders"
							:key="'f-' + folder.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'folder' && dragItem.id === folder.id, 'drag-over': dragOverFolder === folder.id }"
							draggable="true"
							@click="navigateToFolder(folder.id)"
							@contextmenu.prevent="showFolderMenu($event, folder)"
							@dragstart="onItemDragStart($event, 'folder', folder)"
							@dragend="onItemDragEnd"
							@dragover.prevent.stop="onFolderDragOver($event, folder)"
							@dragleave.prevent.stop="onFolderDragLeave"
							@drop.prevent.stop="onFolderDrop($event, folder)"
						>
							<td style="width: 40px"><v-icon color="amber darken-2">mdi-folder</v-icon></td>
							<td>{{ folder.name }}</td>
							<td class="text-right">
								<v-tooltip bottom v-if="folder.portal_shares_count > 0">
									<template v-slot:activator="{ on }">
										<v-badge :content="folder.portal_shares_count" color="teal" inline v-on="on" class="mr-1">
											<v-icon x-small color="teal">mdi-account-multiple</v-icon>
										</v-badge>
									</template>
									<span>Condiviso con {{ folder.portal_shares_count }} utente/i del portale</span>
								</v-tooltip>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small :color="folder.area === 'tenant' ? 'blue' : 'green'">mdi-circle</v-icon>
									</template>
									<span>{{ folder.area === 'tenant' ? 'Caricati da ' + appTitle : 'Caricati dal cliente' }}</span>
								</v-tooltip>
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Documenti -->
			<div v-if="filteredDocuments.length">
				<div class="text-overline grey--text mb-2">Documenti</div>
				<!-- Vista griglia -->
				<v-row v-if="viewMode === 'grid'" dense>
					<v-col v-for="doc in filteredDocuments" :key="'d-' + doc.id" cols="6" sm="4" md="3" lg="2">
						<v-card
							outlined
							class="dms-doc-card"
							:class="{ 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id, 'selected': selectedDoc && selectedDoc.id === doc.id, 'selected-multi': selectedDocIds.includes(doc.id) }"
							:data-doc-id="doc.id"
							draggable="true"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
							@click="toggleSelectDocument(doc)"
							@dblclick.stop="isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
						>
							<div class="dms-doc-preview">
								<img v-if="doc.thumbnail" :src="'data:image/png;base64,' + doc.thumbnail" />
								<v-icon v-else size="64" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<v-tooltip bottom v-if="doc.is_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="primary" style="position: absolute; top: 8px; right: 8px">mdi-share-variant</v-icon>
									</template>
									<span>Condiviso dalla libreria</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.expires_at">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="orange" style="position: absolute; top: 8px; left: 8px">mdi-calendar-alert</v-icon>
									</template>
									<span>Scadenza: {{ formatDateShort(doc.expires_at) }}</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.available_until">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="purple" style="position: absolute; bottom: 8px; right: 8px">mdi-calendar-lock</v-icon>
									</template>
									<span>Disponibile fino al: {{ formatDateShort(doc.available_until) }}</span>
								</v-tooltip>
								<div class="dms-doc-tags" v-if="doc.tags && doc.tags.length">
									<v-chip v-for="tag in doc.tags.slice(0, 2)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white">{{ tag.name }}</v-chip>
									<v-chip v-if="doc.tags.length > 2" x-small color="grey" text-color="white">+{{ doc.tags.length - 2 }}</v-chip>
								</div>
							</div>
							<div class="dms-doc-info">
								<v-icon size="18" :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon>
								<span class="text-body-2 text-truncate" style="flex: 1; min-width: 0">{{ doc.name | fileName }}.{{ doc.ext }}</span>
								<v-tooltip bottom v-if="doc.portal_shares_count > 0">
									<template v-slot:activator="{ on }">
										<v-badge :content="doc.portal_shares_count" color="teal" inline v-on="on" class="flex-shrink-0">
											<v-icon x-small color="teal">mdi-account-multiple</v-icon>
										</v-badge>
									</template>
									<span>Condiviso con {{ doc.portal_shares_count }} utente/i del portale</span>
								</v-tooltip>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small :color="doc.area === 'tenant' ? 'blue' : 'green'" class="flex-shrink-0">mdi-circle</v-icon>
									</template>
									<span>{{ doc.area === 'tenant' ? 'Caricati da ' + appTitle : 'Caricati dal cliente' }}</span>
								</v-tooltip>
								<v-btn icon x-small @click.stop="showDocMenu($event, doc)"><v-icon>mdi-dots-vertical</v-icon></v-btn>
							</div>
						</v-card>
					</v-col>
				</v-row>
				<!-- Vista lista -->
				<v-simple-table v-else dense class="bordered-table">
					<thead>
						<tr>
							<th style="width: 40px"></th>
							<th>Nome</th>
							<th v-if="isGlobalSearch" class="d-none d-sm-table-cell">Cartella</th>
							<th class="d-none d-sm-table-cell">Dimensione</th>
							<th class="d-none d-sm-table-cell">Area</th>
							<th class="d-none d-sm-table-cell">Data</th>
							<th class="d-none d-md-table-cell">Caricato da</th>
							<th class="d-none d-md-table-cell">Tag</th>
							<th style="width: 60px"></th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="doc in filteredDocuments"
							:key="'d-' + doc.id"
							class="cursor-pointer"
							:class="{ 'dragging': dragItem && dragItem.type === 'doc' && dragItem.id === doc.id }"
							draggable="true"
							@click="debouncedOpenDocDetails(doc)"
							@dblclick.stop="cancelDebouncedClick(); isPreviewable(doc) ? openPreview(doc) : downloadDoc(doc)"
							@contextmenu.prevent="showDocMenu($event, doc)"
							@dragstart="onItemDragStart($event, 'doc', doc)"
							@dragend="onItemDragEnd"
						>
							<td><v-icon :color="getFileColor(doc.ext)">{{ getFileIcon(doc.ext) }}</v-icon></td>
							<td>
								{{ doc.name | fileName(doc.ext) }}
								<v-tooltip bottom v-if="doc.is_shared">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="primary" class="ml-1">mdi-share-variant</v-icon>
									</template>
									<span>Condiviso dalla libreria</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.portal_shares_count > 0">
									<template v-slot:activator="{ on }">
										<v-badge :content="doc.portal_shares_count" color="teal" inline v-on="on" class="ml-1">
											<v-icon x-small color="teal">mdi-account-multiple</v-icon>
										</v-badge>
									</template>
									<span>Condiviso con {{ doc.portal_shares_count }} utente/i del portale</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.expires_at">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="orange" class="ml-1">mdi-calendar-alert</v-icon>
									</template>
									<span>Scadenza: {{ formatDateShort(doc.expires_at) }}</span>
								</v-tooltip>
								<v-tooltip bottom v-if="doc.available_until">
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small color="purple" class="ml-1">mdi-calendar-lock</v-icon>
									</template>
									<span>Disponibile fino al: {{ formatDateShort(doc.available_until) }}</span>
								</v-tooltip>
							</td>
							<td v-if="isGlobalSearch" class="d-none d-sm-table-cell text-no-wrap text-caption">
								<v-icon x-small class="mr-1">mdi-folder</v-icon>{{ doc.folder ? doc.folder.name : 'Home' }}
							</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ formatSize(doc.size) }}</td>
							<td class="d-none d-sm-table-cell">
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-icon v-on="on" x-small :color="doc.area === 'tenant' ? 'blue' : 'green'">mdi-circle</v-icon>
									</template>
									<span>{{ doc.area === 'tenant' ? 'Caricati da ' + appTitle : 'Caricati dal cliente' }}</span>
								</v-tooltip>
							</td>
							<td class="d-none d-sm-table-cell text-no-wrap">{{ doc.created_at | date }}</td>
							<td class="d-none d-md-table-cell text-no-wrap">{{ doc.uploaded_by ? doc.uploaded_by.name : '-' }}</td>
							<td class="d-none d-md-table-cell">
								<v-chip v-for="tag in (doc.tags || []).slice(0, 3)" :key="tag.id" x-small :color="tag.color || 'grey'" text-color="white" class="mr-1">
									{{ tag.name }}
								</v-chip>
								<span v-if="(doc.tags || []).length > 3" class="text-caption grey--text">+{{ doc.tags.length - 3 }}</span>
							</td>
							<td>
								<v-btn icon x-small @click.stop="downloadDoc(doc)"><v-icon small>mdi-download</v-icon></v-btn>
							</td>
						</tr>
					</tbody>
				</v-simple-table>
			</div>

			<!-- Empty state -->
			<div v-if="!loading && !filteredFolders.length && !filteredDocuments.length" class="empty-state text-center">
				<v-icon size="64" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
				<div class="text-h6 grey--text mt-3">Nessun contenuto</div>
				<div class="text-body-2 grey--text">Trascina file qui o usa il pulsante Carica</div>
			</div>

			<v-progress-circular v-if="loading" indeterminate color="primary" class="d-block mx-auto my-12"></v-progress-circular>

			<!-- Drag overlay -->
			<div v-if="isDragging" class="drop-overlay">
				<v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
				<div class="text-h6 primary--text mt-2">Rilascia i file qui</div>
			</div>
		</div>

		<!-- Context menu -->
		<v-menu v-model="contextMenu.show" :position-x="contextMenu.x" :position-y="contextMenu.y" absolute content-class="round-card">
			<v-list dense>
				<v-list-item @click="openPreview(contextMenu.item)" v-if="contextMenu.type === 'doc' && isPreviewable(contextMenu.item)">
					<v-list-item-icon><v-icon small>mdi-eye</v-icon></v-list-item-icon>
					<v-list-item-title>Anteprima</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openDocDetails(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-information-outline</v-icon></v-list-item-icon>
					<v-list-item-title>Dettagli</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadDoc(contextMenu.item)" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadFolder(contextMenu.item)" v-if="contextMenu.type === 'folder'">
					<v-list-item-icon><v-icon small>mdi-folder-download</v-icon></v-list-item-icon>
					<v-list-item-title>Scarica cartella</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openRenameDialog()">
					<v-list-item-icon><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
					<v-list-item-title>Rinomina</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openMoveDialog()">
					<v-list-item-icon><v-icon small>mdi-folder-move</v-icon></v-list-item-icon>
					<v-list-item-title>Sposta</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openShareDialog()">
					<v-list-item-icon><v-icon small>mdi-account-multiple-plus</v-icon></v-list-item-icon>
					<v-list-item-title>Condividi</v-list-item-title>
				</v-list-item>
				<v-list-item @click="openTagDialog()" v-if="contextMenu.type === 'doc'">
					<v-list-item-icon><v-icon small>mdi-tag</v-icon></v-list-item-icon>
					<v-list-item-title>Tag</v-list-item-title>
				</v-list-item>
				<v-list-item @click="confirmDelete()">
					<v-list-item-icon><v-icon small color="red">mdi-delete</v-icon></v-list-item-icon>
					<v-list-item-title class="red--text">Elimina</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>

		<!-- Dettagli documento (sidebar) -->
		<v-navigation-drawer v-model="detailDrawer" right app clipped temporary width="380">
			<template v-if="selectedDoc">
				<v-card flat class="background-1">
					<v-card-title class="d-flex align-center" style="flex-wrap: nowrap">
						<v-icon left :color="getFileColor(selectedDoc.ext)" class="flex-shrink-0">{{ getFileIcon(selectedDoc.ext) }}</v-icon>
						<span class="text-subtitle-1 text-truncate" style="min-width: 0; flex: 1">{{ selectedDoc.name | fileName(selectedDoc.ext) }}</span>
						<v-btn icon small class="flex-shrink-0 ml-auto" @click="detailDrawer = false"><v-icon>mdi-close</v-icon></v-btn>
					</v-card-title>
					<v-divider></v-divider>
					<v-card-text>
						<div class="mb-3">
							<div class="text-caption grey--text">Nome file</div>
							<div>{{ selectedDoc.name | fileName(selectedDoc.ext) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Dimensione</div>
							<div>{{ formatSize(selectedDoc.size) }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Data caricamento</div>
							<div>{{ selectedDoc.created_at | date }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Caricato da</div>
							<div>{{ selectedDoc.uploaded_by ? selectedDoc.uploaded_by.name : '-' }}</div>
						</div>
						<div class="mb-3" v-if="selectedDoc.description">
							<div class="text-caption grey--text">Descrizione</div>
							<div>{{ selectedDoc.description }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Versione</div>
							<div>{{ selectedDoc.version }}</div>
						</div>
						<div class="mb-3">
							<div class="text-caption grey--text">Area</div>
							<v-chip x-small :color="selectedDoc.area === 'tenant' ? 'blue' : 'green'" text-color="white">
								{{ selectedDoc.area === 'tenant' ? 'Caricati da ' + appTitle : 'Caricati dal cliente' }}
							</v-chip>
						</div>
						<div class="mb-3" v-if="selectedDoc.tags && selectedDoc.tags.length">
							<div class="text-caption grey--text">Tag</div>
							<v-chip v-for="tag in selectedDoc.tags" :key="tag.id" small :color="tag.color || 'grey'" text-color="white" class="mr-1 mt-1">
								{{ tag.name }}
							</v-chip>
						</div>

						<!-- Scadenza documento -->
						<div class="mb-3">
							<div class="text-caption grey--text mb-1">Data di scadenza</div>
							<gmh-date-picker
								:value="selectedDoc.expires_at ? selectedDoc.expires_at.substring(0, 10) : null"
								@input="updateDocDates({ expires_at: $event })"
								@clear="updateDocDates({ expires_at: null, expiry_reminder_days: null })"
								placeholder="Nessuna scadenza"
								prepend-icon="mdi-calendar-alert"
								:append-icon="null"
								:min="today"
								hide-details
								:picker-props="{ 'no-title': true }"
							></gmh-date-picker>
						</div>
						<div class="mb-3" v-if="selectedDoc.expires_at">
							<v-text-field
								type="number"
								:value="selectedDoc.expiry_reminder_days"
								@change="updateDocDates({ expiry_reminder_days: $event || null })"
								placeholder="Usa impostazione globale"
								prepend-icon="mdi-bell-outline"
								label="Giorni reminder"
								dense
								outlined
								clearable
								hide-details
								min="1"
								max="90"
							></v-text-field>
						</div>
						<div class="mb-3" v-if="selectedDoc.expires_at">
							<v-btn
								small
								text
								color="orange"
								@click="openNotifyDialog()"
							>
								<v-icon left small>mdi-bell-ring</v-icon>
								Invia notifica scadenza
							</v-btn>
						</div>

						<!-- Fine disponibilità -->
						<div class="mb-3">
							<div class="text-caption grey--text mb-1">Fine disponibilità</div>
							<gmh-date-picker
								:value="selectedDoc.available_until ? selectedDoc.available_until.substring(0, 10) : null"
								@input="updateDocDates({ available_until: $event })"
								@clear="updateDocDates({ available_until: null })"
								placeholder="Sempre disponibile"
								prepend-icon="mdi-calendar-lock"
								:append-icon="null"
								:min="today"
								hide-details
								:picker-props="{ 'no-title': true }"
							></gmh-date-picker>
						</div>

						<!-- Versioni -->
						<div class="mb-3" v-if="docVersions.length">
							<div class="text-caption grey--text mb-1">Versioni precedenti</div>
							<v-list dense class="pa-0">
								<v-list-item v-for="ver in docVersions" :key="ver.id" dense>
									<v-list-item-content>
										<v-list-item-title class="text-body-2">v{{ ver.version }} - {{ ver.original_name }}</v-list-item-title>
										<v-list-item-subtitle>{{ ver.created_at | date }} - {{ formatSize(ver.size) }}</v-list-item-subtitle>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</div>
					</v-card-text>
					<v-card-actions>
						<v-btn color="primary" outlined @click="downloadDoc(selectedDoc)">
							<v-icon left small>mdi-download</v-icon>Scarica
						</v-btn>
						<v-btn color="success" text @click="$refs.versionInput.click()">
							<v-icon left small>mdi-upload</v-icon>Nuova Versione
						</v-btn>
						<input
							ref="versionInput"
							type="file"
							style="position: absolute; width: 0; height: 0; overflow: hidden; opacity: 0"
							@change="handleVersionUpload"
						/>
					</v-card-actions>
				</v-card>
			</template>
		</v-navigation-drawer>

		<DmsRecentActivity ref="recentActivity" :api-base="apiBase" />

		<!-- Dialog nuova cartella -->
		<v-dialog v-model="newFolderDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Nuova Cartella</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field v-model="newFolderName" label="Nome cartella" outlined dense autofocus @keyup.enter="createFolder"></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="newFolderDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="createFolder" :loading="saving">Crea</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog rinomina -->
		<v-dialog v-model="renameDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Rinomina</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-text-field v-model="renameName" label="Nuovo nome" outlined dense autofocus @keyup.enter="renameItem" :rules="[v => !/[\\\\/:*?&quot;<>|]/.test(v) || 'Caratteri non consentiti: \\\\ / : * ? &quot; < > |']"></v-text-field>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="renameDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="renameItem" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog tag -->
		<v-dialog v-model="tagDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Gestisci Tag</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-combobox
						ref="tagCombobox"
						v-model="selectedTags"
						:items="allTags"
						item-text="name"
						item-value="id"
						label="Tag"
						multiple
						chips
						small-chips
						deletable-chips
						outlined
						dense
					></v-combobox>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="tagDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="saveTags" :loading="saving">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog sposta -->
		<v-dialog v-model="moveDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Sposta in...</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<v-list dense class="pa-0">
						<v-list-item @click="moveFolderTarget = null" :class="{ 'v-list-item--active primary--text': moveFolderTarget === null }">
							<v-list-item-icon class="mr-2 pr-2"><v-icon color="amber darken-2">mdi-folder-home</v-icon></v-list-item-icon>
							<v-list-item-title>Home</v-list-item-title>
						</v-list-item>
					</v-list>
					<v-treeview
						:items="folderTree"
						item-key="id"
						item-text="name"
						activatable
						@update:active="moveFolderTarget = $event[0] || null"
						open-on-click
						dense
					>
						<template v-slot:prepend="{}">
							<v-icon color="amber darken-2">mdi-folder</v-icon>
						</template>
					</v-treeview>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="moveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="moveItem" :loading="saving">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog importa dalla libreria -->
		<library-import-dialog
			v-model="importDialog"
			:api-base="apiBase"
			:target-folder-id="currentFolderId"
			@imported="onLibraryImported"
			@error="showSnackbar($event, 'error')"
		/>

		<!-- Dialog condivisione con utenti del portale -->
		<portal-share-dialog
			v-model="shareDialog"
			:company-id="companyId"
			:tipo="shareTipo"
			:shareable-id="shareId"
			@saved="onShared"
		/>

		<!-- Dialog conferma eliminazione -->
		<v-dialog v-model="deleteDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma Eliminazione</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Sei sicuro di voler eliminare <strong>{{ contextMenu.item ? contextMenu.item.name : '' }}</strong>?
					<span v-if="contextMenu.type === 'folder'">Tutti i contenuti della cartella verranno eliminati.</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="deleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="deleteItem" :loading="saving">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog conferma notifica scadenza -->
		<v-dialog v-model="notifyConfirmDialog" max-width="400" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma Invio Notifica</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<p class="mb-2">Seleziona i destinatari della notifica.</p>
					<p class="text-caption grey--text mb-3">Lascia vuoto per inviare a tutti gli utenti portale del cliente.</p>
					<v-autocomplete
						v-model="notifyUserIds"
						:items="notifyPortalUsers"
						item-text="text"
						item-value="id"
						:loading="notifyUsersLoading"
						label="Destinatari"
						placeholder="Tutti gli utenti portale"
						multiple
						chips
						small-chips
						deletable-chips
						outlined
						dense
						hide-no-data
						clearable
					>
						<template v-slot:item="{ item }">
							<v-list-item-content>
								<v-list-item-title>{{ item.text }}</v-list-item-title>
								<v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
							</v-list-item-content>
						</template>
					</v-autocomplete>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="notifyConfirmDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="confirmSendExpiryNotification" :loading="notifyLoading">Invia</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: bulk move -->
		<v-dialog v-model="bulkMoveDialog" max-width="500" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Sposta {{ selectionCount }} elemento/i</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					<p class="mb-2">Scegli la cartella di destinazione.</p>
					<v-select
						v-model="bulkMoveFolderTarget"
						:items="[{ id: null, name: '— Radice —' }].concat(folders)"
						item-text="name"
						item-value="id"
						label="Cartella destinazione"
						:menu-props="{'offset-y': true}"
						outlined dense clearable
					></v-select>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkMoveDialog = false">Annulla</v-btn>
					<v-btn text color="info" @click="confirmBulkMove" :loading="bulkLoading">Sposta</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog: bulk delete -->
		<v-dialog v-model="bulkDeleteDialog" max-width="450" persistent>
			<v-card class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Conferma eliminazione</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="pt-4 body-2">
					Eliminare <strong>{{ selectedDocIds.length }}</strong> documento/i e <strong>{{ selectedFolderIds.length }}</strong> cartella/e?
					<span v-if="selectedFolderIds.length">
						<br><br><v-icon small color="warning">mdi-alert</v-icon>
						Le cartelle saranno eliminate con tutto il loro contenuto.
					</span>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="primary" @click="bulkDeleteDialog = false">Annulla</v-btn>
					<v-btn text color="error" @click="confirmBulkDelete" :loading="bulkLoading">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<file-preview-dialog
			v-model="previewDialog"
			:doc="previewDoc"
			:url="previewUrl"
		/>

		<v-snackbar v-model="snackbar.value" :color="snackbar.color" :timeout="snackbar.timeout || 4000">
			{{ snackbar.text }}
			<template v-slot:action="{ attrs }">
				<v-btn dark icon v-bind="attrs" @click="snackbar.value = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
	</v-container>
	</v-card>
</template>

<script>
import DmsMixin from '@/mixins/DmsMixin'
import DmsRecentActivity from '@/components/common/DmsRecentActivity'
import FilePreviewDialog from '@/components/common/FilePreviewDialog'
import LibraryImportDialog from '@/components/modules/portal/LibraryImportDialog'
import PortalShareDialog from '@/components/modules/portal/PortalShareDialog'

export default {
	name: 'PortalDmsAdminTab',
	mixins: [DmsMixin],
	components: { DmsRecentActivity, FilePreviewDialog, LibraryImportDialog, PortalShareDialog },
	props: {
		companyId: { type: Number, required: true }
	},
	data() {
		return {
			areaFilter: 'all',
			notifyLoading: false,
			notifyConfirmDialog: false,
			notifyUserIds: [],
			notifyPortalUsers: [],
			notifyUsersLoading: false,
			importDialog: false,
			shareDialog: false,
			shareTipo: '',
			shareId: null
		}
	},
	computed: {
		// scadenza e fine disponibilita' non possono essere nel passato
		today() {
			return moment().format('YYYY-MM-DD')
		},
		appTitle() {
			return localStorage.getItem('app_name') || 'GiveMeHive!'
		},
		filteredFolders() {
			var items = this.folders
			if (this.search) {
				var s = this.search.toLowerCase()
				items = items.filter(f => f.name.toLowerCase().includes(s))
			}
			if (this.areaFilter !== 'all') {
				items = items.filter(f => f.area === this.areaFilter)
			}
			return items
		},
		filteredDocuments() {
			if (this.search && this.searchScope === 'global') {
				return this.searchResults
			}
			var items = this.documents
			if (this.search) {
				var s = this.search.toLowerCase()
				items = items.filter(d =>
					d.name.toLowerCase().includes(s) ||
					(d.tags && d.tags.some(t => t.name.toLowerCase().includes(s)))
				)
			}
			if (this.areaFilter !== 'all') {
				items = items.filter(d => d.area === this.areaFilter)
			}
			return items
		},
		filteredStorageSize() {
			if (this.areaFilter === 'tenant') return this.storage.tenant
			if (this.areaFilter === 'client') return this.storage.client
			return this.storage.total
		}
	},
	methods: {
		dmsConfig() {
			return {
				apiBase: 'portal-dms/' + this.companyId,
				breadcrumbRootLabel: 'Home',
				viewModeStorageKey: 'portal-dms-view-mode',
				hasRecentActivity: true
			}
		},
		loadContent() {
			this.loading = true
			this.axios.get(this.apiBase + '/folders', {
				params: { parent_id: this.currentFolderId }
			}).then(resp => {
				if (resp.data.success) {
					this.folders = resp.data.data.folders || []
					this.documents = resp.data.data.documents || []
					this.breadcrumb = resp.data.data.breadcrumb || []
					this.storage = resp.data.data.storage || { tenant: 0, client: 0, total: 0 }
				}
			}).catch(() => {}).finally(() => { this.loading = false })
		},
		// Import dalla libreria: il dialog gestisce albero e selezione
		onLibraryImported(messaggio) {
			this.showSnackbar(messaggio, 'success')
			this.loadContent()
			this.onAfterMutation()
		},
		// Condivisione con utenti del portale: valorizza tipo/id dall'elemento
		// selezionato nel context menu e apre il dialog dedicato
		openShareDialog() {
			this.shareTipo = this.contextMenu.type === 'folder' ? 'folder' : 'attachment'
			this.shareId = this.contextMenu.item.id
			this.shareDialog = true
		},
		onShared() {
			this.loadContent()
		},
		formatDateShort(date) {
			if (!date) return ''
			return new Date(date).toLocaleDateString('it-IT', {
				day: '2-digit', month: '2-digit', year: 'numeric'
			})
		},
		async updateDocDates(fields) {
			try {
				var resp = await this.axios.put(
					this.apiBase + '/documents/' + this.selectedDoc.id,
					fields
				)
				if (resp.data.success) {
					Object.assign(this.selectedDoc, fields)
					this.loadContent()
				}
			} catch (e) {
				this.$store.commit('setSnackbarError')
			}
		},
		async openNotifyDialog() {
			this.notifyUserIds = []
			this.notifyConfirmDialog = true
			this.notifyUsersLoading = true
			try {
				var resp = await this.axios.get(this.apiBase + '/portal-users')
				this.notifyPortalUsers = resp.data.users || []
			} catch (e) {
				this.notifyPortalUsers = []
			} finally {
				this.notifyUsersLoading = false
			}
		},
		async confirmSendExpiryNotification() {
			this.notifyLoading = true
			try {
				var payload = {}
				if (this.notifyUserIds && this.notifyUserIds.length) {
					payload.user_ids = this.notifyUserIds
				}
				var resp = await this.axios.post(
					this.apiBase + '/documents/' + this.selectedDoc.id + '/notify-expiry',
					payload
				)
				if (resp.data.success) {
					this.notifyConfirmDialog = false
					this.$store.commit('setSnackbar', {
						timeout: 4000,
						text: 'Notifica inviata',
						value: true,
						color: 'success'
					})
				}
			} catch (e) {
				this.$store.commit('setSnackbarError')
			}
			this.notifyLoading = false
		}
	}
}
</script>

<style src="@/css/dms.css"></style>

<style scoped>
.marquee-rect {
	position: absolute;
	border: 1px dashed var(--v-primary-base, #1976d2);
	background: rgba(25, 118, 210, 0.08);
	pointer-events: none;
	z-index: 5;
}
.dms-doc-card.selected-multi,
.v-card.selected-multi {
	outline: 2px solid var(--v-primary-base, #1976d2);
	outline-offset: -2px;
}
.bulk-toolbar {
	position: sticky;
	top: 0;
	z-index: 4;
}
</style>
